'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BlogPostForm, type BlogPostFormRef } from '@/components/partials/blog-posts'
import { useCreateBlogPost } from '@/hooks'
import { type CreateBlogPostParams } from '@/types/blog-post'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const blogPostFormRef = useRef<BlogPostFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createBlogPost, isPending: isLoading } = useCreateBlogPost()

  const handleSubmit = (data: CreateBlogPostParams) => {
    createBlogPost(data, {
      onSuccess: async newBlogPost => {
        setIsRedirecting(true)
        queryClient.setQueryData(['blog-post', newBlogPost.id], newBlogPost)
        await queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
        toast.success(t('created_successfully'))
        router.push(`/blog-posts/${newBlogPost.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateBlogPostParams) => {
    createBlogPost(data, {
      onSuccess: async newBlogPost => {
        queryClient.setQueryData(['blog-post', newBlogPost.id], newBlogPost)
        await queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
        const detailUrl = `/blog-posts/${newBlogPost.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        blogPostFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (blogPostFormRef.current) {
        blogPostFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/blog-posts')
  }

  return (
    <BlogPostForm
      key={formKey}
      ref={blogPostFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
